import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Newsletter from "../components/Newsletter";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (p) => `${BACKEND}/api/static/img/${p}`;

export default function Manifeste() {
    const SITE = process.env.REACT_APP_SITE_URL || "https://coteperdue.com";
    return (
        <div data-testid="manifeste-page" className="bg-cp-ivoire">
            <Helmet>
                <title>Manifeste · Côte Perdue — La côte de Normandie & Picardie qui revient</title>
                <meta name="description" content="Entre Le Tréport, Mers-les-Bains, Cayeux-sur-Mer, Eu, Blangy-sur-Bresle et Aumale : 80 km de côte normande et picarde qui réunissent falaises de craie, villas Belle Époque, châteaux royaux, jardins remarquables et tables d'auteur. Notre vision." />
                <meta name="keywords" content="Côte Perdue, Normandie Picardie, Le Tréport, Mers-les-Bains, Cayeux-sur-Mer, Eu, Blangy-sur-Bresle, Aumale, Bois de Cise, falaises craie, villas Belle Époque, week-end Normandie, manifeste" />
                <meta property="og:title" content="Manifeste — La côte chic discrète de Normandie & Picardie" />
                <meta property="og:description" content="80 km de côte qui réunissent Haute-Normandie et Picardie, du Tréport à Cayeux. Patrimoine, jardins remarquables, villas Belle Époque, gastronomie." />
                <meta property="og:type" content="article" />
                <link rel="canonical" href={`${SITE}/manifeste`} />
            </Helmet>

            {/* HERO — full-bleed cliff image */}
            <section className="relative min-h-[88vh] bg-cp-encre text-cp-ivoire overflow-hidden cp-grain">
                <img
                    src={img("hero_cliffs_cinematic.jpg")}
                    alt="Falaises de craie entre Le Tréport, Mers-les-Bains et Ault — Côte Perdue"
                    className="absolute inset-0 w-full h-full object-cover cp-kenburns-slow cp-photo-warm"
                />
                <div className="absolute inset-0 cp-hero-overlay" />

                <div className="relative z-10 min-h-[88vh] max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20 flex flex-col justify-end">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <p className="cp-tiny text-cp-sable mb-10">Manifeste · Numéro 01 · Hiver 2026</p>
                        <h1 className="cp-serif text-[60px] sm:text-[88px] md:text-[120px] lg:text-[160px] xl:text-[192px] leading-[0.86] tracking-[-0.04em] max-w-6xl">
                            Une côte, <em className="cp-display-italic text-cp-sable">deux régions</em>,<br />
                            <em className="cp-display-italic text-cp-sable">un même</em> regard.
                        </h1>
                        <p className="cp-serif italic text-xl md:text-3xl font-light text-cp-ivoire/85 leading-[1.4] mt-10 max-w-3xl">
                            De la Côte d'Albâtre à la Baie de Somme, 80 km où la Haute-Normandie et la Picardie partagent les mêmes falaises, les mêmes villas et le même goût pour ce qui ne se montre pas.
                        </p>
                    </motion.div>
                </div>

                <div className="absolute bottom-10 right-6 md:right-12 flex flex-col items-end gap-1 text-cp-ivoire/70">
                    <span className="cp-tiny text-cp-sable">5 min de lecture</span>
                    <span className="cp-tiny">Numéro I · 2026</span>
                </div>
            </section>

            {/* BODY — long-form editorial */}
            <article className="relative max-w-3xl mx-auto px-6 md:px-0 py-24 md:py-40">
                <div className="space-y-14 cp-serif text-xl md:text-2xl leading-[1.6] font-light text-cp-encre">

                    {/* Drop cap — heritage */}
                    <p className="first-letter:cp-serif first-letter:text-[112px] first-letter:leading-[0.78] first-letter:float-left first-letter:pr-4 first-letter:pt-2 first-letter:text-cp-foret first-letter:font-normal">
                        Entre <em className="cp-display-italic">Le Tréport</em> et <em className="cp-display-italic">Cayeux-sur-Mer</em>, la frontière administrative entre la <em className="cp-display-italic">Seine-Maritime</em> et la <em className="cp-display-italic">Somme</em> coupe une plage en deux. Personne, ici, n'y prête attention. Les falaises continuent, les villas se répondent, les marées sont les mêmes. Cette côte n'a jamais cessé d'être <em className="cp-display-italic">une seule</em> — la nôtre.
                    </p>

                    <hr className="border-cp-border" />

                    <h2 className="cp-serif text-3xl md:text-4xl italic text-cp-foret leading-tight">
                        L'histoire d'une élégance partagée
                    </h2>
                    <p>
                        En 1872, l'architecte <em className="cp-display-italic">Edmond Lejeune</em> dessine d'un trait Mers-les-Bains : six cents <em className="cp-display-italic">villas Belle Époque</em>, un casino, une esplanade. Le chic parisien y descend. À deux kilomètres, le <em className="cp-display-italic">Château d'Eu</em> abrite Louis-Philippe et la reine Victoria — la France et l'Angleterre s'y rencontrent l'été. À l'autre bout, à Cayeux, on étire la plus longue plage de galets d'Europe et ses 524 cabines polychromes.
                    </p>
                    <p>
                        En 600 mètres on passe du domaine royal de Louis-Philippe à la station inventée par les industriels du Nord ; en 30 km de la <em className="cp-display-italic">forêt d'Eu</em> aux dunes de la Baie de Somme. Un patrimoine continu, sans frontière, que <em className="cp-display-italic">la Manche</em> et la <em className="cp-display-italic">Bresle</em> rythment depuis toujours.
                    </p>

                    <hr className="border-cp-border" />

                    <h2 className="cp-serif text-3xl md:text-4xl italic text-cp-foret leading-tight">
                        Pourquoi maintenant
                    </h2>
                    <p>
                        Parce que la nouvelle élégance ne crie plus. Elle se déplace en covoiturage régional ou à pied, déjeune chez le boulanger qui maîtrise son levain, dort dans une maison d'hôtes plutôt qu'un palace, marche au lieu de réserver un Spa. Cette côte coche toutes ces cases — <em className="cp-display-italic">sans avoir essayé</em>.
                    </p>
                    <p>
                        À deux heures de Paris : <em className="cp-display-italic">le Manoir d'Ango</em> de la Renaissance, <em className="cp-display-italic">le Bois des Moutiers</em> dessiné par Lutyens et Jekyll, le <em className="cp-display-italic">Domaine de Joinville</em> de Louis-Philippe, l'<em className="cp-display-italic">Hôtel Le Cise</em> niché dans son bois face à la mer, le <em className="cp-display-italic">Château de Rambures</em> dans la Somme, la <em className="cp-display-italic">Vallée du Verre</em> où 75 % des flacons de parfum du monde sont encore soufflés à la canne.
                    </p>
                    <p>
                        On peut le dire autrement : <em className="cp-display-italic text-cp-foret">il y a tout, et personne n'en parle.</em>
                    </p>

                    <hr className="border-cp-border" />

                    <h2 className="cp-serif text-3xl md:text-4xl italic text-cp-foret leading-tight">
                        Ce que Côte Perdue est, et n'est pas
                    </h2>
                    <p>
                        Pas un guide qui empile. Pas une appli qui géolocalise. Pas un catalogue.
                    </p>
                    <p>
                        <em className="cp-display-italic text-cp-foret">Un magazine vivant</em> qui assume une ligne : nous gardons les adresses notées 4,5 sur 5 par <em className="cp-display-italic">au moins cinquante personnes réelles</em>, nous écartons les fast-foods et les chaînes, nous vérifions les photos à l'œil. Chaque fiche renvoie à la source — site officiel, réservation directe, numéro qui sonne. Nous ne prenons aucune commission cachée.
                    </p>
                    <p>
                        Et nous tenons un parti pris : <em className="cp-display-italic">la Normandie et la Picardie</em>, ici, ne sont pas deux rives mais une même conversation entre une falaise et un estuaire.
                    </p>

                    <hr className="border-cp-border" />

                    <div className="bg-cp-craie -mx-6 md:-mx-20 px-6 md:px-20 py-14 md:py-20">
                        <p className="cp-serif italic text-2xl md:text-4xl text-cp-foret leading-[1.35]">
                            Une côte de craie, deux régions, un seul week-end. Vous n'avez plus qu'à choisir <em className="cp-display-italic">quel samedi</em>.
                        </p>
                        <p className="cp-tiny text-cp-gris mt-10">Signé — la rédaction de Côte Perdue · Numéro I, hiver 2026</p>
                    </div>

                </div>
            </article>

            {/* CTA grid — three entry points */}
            <section className="bg-cp-encre text-cp-ivoire py-24 md:py-32 cp-grain">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <p className="cp-tiny text-cp-sable mb-10">Trois manières d'entrer</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
                        {[
                            { to: "/destinations", label: "Les 16 communes du territoire", sub: "De Berneval à Cayeux — Seine-Maritime + Somme, sans frontière" },
                            { to: "/adresses", label: "289 adresses vérifiées", sub: "Note Google ≥ 4,5/5 · audit photo · vraies devantures" },
                            { to: "/club", label: "Rejoindre le Cercle", sub: "Notre Club. Limité à 100 Fondateurs." },
                        ].map((c, i) => (
                            <Link
                                key={i}
                                to={c.to}
                                data-testid={`manifeste-cta-${i}`}
                                className="group block border-t border-cp-ivoire/20 pt-8 hover:border-cp-sable transition-colors"
                            >
                                <span className="cp-tiny text-cp-sable mb-4 block">N°0{i + 1}</span>
                                <h3 className="cp-serif text-3xl md:text-4xl leading-tight text-cp-ivoire group-hover:text-cp-sable transition-colors mb-3">
                                    {c.label}
                                </h3>
                                <p className="cp-serif italic text-cp-ivoire/60">{c.sub}</p>
                                <span className="cp-tiny text-cp-sable mt-5 inline-block opacity-0 group-hover:opacity-100 transition-opacity">Entrer →</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="bg-cp-ivoire py-20 md:py-28">
                <div className="max-w-[1000px] mx-auto px-6 md:px-12">
                    <p className="cp-tiny text-cp-foret mb-6">La lettre de Côte Perdue</p>
                    <h3 className="cp-serif text-5xl md:text-7xl leading-[0.92] text-cp-encre mb-4">
                        Une <em className="cp-display-italic">lettre</em>. Par mois.
                    </h3>
                    <p className="cp-script text-2xl text-cp-sable-deep mb-10">Rien d'autre.</p>
                    <div className="max-w-xl">
                        <Newsletter variant="light" source="manifeste" />
                    </div>
                </div>
            </section>
        </div>
    );
}
